import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { 
  History, Filter, Download, Trash2, RefreshCw, User, FileText, 
  Plus, Edit, X, LogIn, Key, ChevronLeft, ChevronRight
} from "lucide-react";
import api from "@/services/api";
import { formatDistanceToNow } from "date-fns";

const ACTION_ICONS = {
  create: Plus,
  update: Edit,
  delete: X,
  login: LogIn,
  password_reset: Key,
  clear_audit_logs: Trash2
};

const ACTION_COLORS = {
  create: "bg-green-100 text-green-700",
  update: "bg-blue-100 text-blue-700",
  delete: "bg-red-100 text-red-700",
  login: "bg-purple-100 text-purple-700",
  password_reset: "bg-amber-100 text-amber-700",
  clear_audit_logs: "bg-gray-100 text-gray-700"
};

const ENTITY_LABELS = {
  food: "Food",
  recipe: "Recipe",
  rescueAction: "Rescue Action",
  combo: "Smart Combo",
  tip: "Daily Tip",
  config: "App Config",
  adminUser: "Admin User",
  admin: "Admin Login",
  system: "System"
};

const AuditLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    entityType: "",
    action: "",
    adminUsername: ""
  });
  const [showDetails, setShowDetails] = useState(null);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearing, setClearing] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (filters.entityType) params.entity_type = filters.entityType;
      if (filters.action) params.action = filters.action;
      if (filters.adminUsername) params.admin_username = filters.adminUsername;

      const [logsRes, summaryRes] = await Promise.all([
        api.get('/admin/audit-logs', { params }),
        api.get('/admin/audit-logs/summary')
      ]);
      
      setLogs(logsRes.data.logs || []);
      setTotalPages(logsRes.data.totalPages || 1);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error("Failed to fetch audit logs:", error);
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleClearOldLogs = async () => {
    setClearing(true);
    try {
      const response = await api.delete('/admin/audit-logs/clear', { params: { days_old: 90 } });
      toast.success(response.data.message);
      setShowClearDialog(false);
      fetchLogs();
    } catch (error) {
      toast.error("Failed to clear old logs");
    } finally {
      setClearing(false);
    }
  };

  const handleExport = () => {
    // Export as JSON
    const dataStr = JSON.stringify(logs, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportName = `carbiq-audit-logs-${new Date().toISOString().split('T')[0]}.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportName);
    linkElement.click();
  };

  const getActionIcon = (action) => {
    const Icon = ACTION_ICONS[action] || FileText;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="p-6 space-y-6" data-testid="audit-log-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
          <p className="text-gray-500">Track all admin actions and changes</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="export-logs-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button 
            variant="outline" 
            className="text-red-600 hover:text-red-700"
            onClick={() => setShowClearDialog(true)}
            data-testid="clear-logs-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear Old Logs
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-500">Total Entries</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <p className="text-2xl font-bold">{summary?.totalLogs?.toLocaleString() || 0}</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-500">Creates</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <p className="text-2xl font-bold text-green-600">{summary?.byAction?.create || 0}</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-500">Updates</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <p className="text-2xl font-bold text-blue-600">{summary?.byAction?.update || 0}</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-500">Logins</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <p className="text-2xl font-bold text-purple-600">{summary?.byAction?.login || 0}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="w-48">
              <Select 
                value={filters.entityType || "all"} 
                onValueChange={(v) => { setFilters({...filters, entityType: v === "all" ? "" : v}); setPage(1); }}
              >
                <SelectTrigger data-testid="filter-entity">
                  <SelectValue placeholder="All Entities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="food">Food</SelectItem>
                  <SelectItem value="recipe">Recipe</SelectItem>
                  <SelectItem value="rescueAction">Rescue Action</SelectItem>
                  <SelectItem value="combo">Smart Combo</SelectItem>
                  <SelectItem value="tip">Daily Tip</SelectItem>
                  <SelectItem value="adminUser">Admin User</SelectItem>
                  <SelectItem value="admin">Admin Login</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select 
                value={filters.action || "all"} 
                onValueChange={(v) => { setFilters({...filters, action: v === "all" ? "" : v}); setPage(1); }}
              >
                <SelectTrigger data-testid="filter-action">
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="create">Create</SelectItem>
                  <SelectItem value="update">Update</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                  <SelectItem value="login">Login</SelectItem>
                  <SelectItem value="password_reset">Password Reset</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select 
                value={filters.adminUsername || "all"} 
                onValueChange={(v) => { setFilters({...filters, adminUsername: v === "all" ? "" : v}); setPage(1); }}
              >
                <SelectTrigger data-testid="filter-admin">
                  <SelectValue placeholder="All Admins" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Admins</SelectItem>
                  {summary?.uniqueAdmins?.map((admin) => (
                    <SelectItem key={admin} value={admin}>{admin}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              variant="ghost" 
              onClick={() => { setFilters({ entityType: "", action: "", adminUsername: "" }); setPage(1); }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Audit Log Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Activity History
          </CardTitle>
          <CardDescription>
            Showing page {page} of {totalPages}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Admin</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Details</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(10)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(6)].map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : logs.length > 0 ? (
                logs.map((log, index) => (
                  <TableRow key={index} data-testid={`audit-row-${index}`}>
                    <TableCell className="text-sm">
                      <div className="flex flex-col">
                        <span>{new Date(log.timestamp).toLocaleString()}</span>
                        <span className="text-xs text-gray-400">
                          {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="font-medium">{log.username}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={ACTION_COLORS[log.action] || "bg-gray-100"}>
                        <span className="flex items-center gap-1">
                          {getActionIcon(log.action)}
                          {log.action}
                        </span>
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {ENTITY_LABELS[log.entityType] || log.entityType}
                      </Badge>
                      <span className="ml-2 text-xs text-gray-500">#{log.entityId}</span>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600 max-w-[200px] truncate">
                      {log.details?.name || log.details?.title || log.details?.newUsername || log.details?.source || "-"}
                    </TableCell>
                    <TableCell>
                      {(log.oldValue || log.newValue) && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => setShowDetails(log)}
                        >
                          View
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No audit logs found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <span className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={!!showDetails} onOpenChange={() => setShowDetails(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto" data-testid="audit-details-dialog">
          <DialogHeader>
            <DialogTitle>Change Details</DialogTitle>
            <DialogDescription>
              {showDetails?.action} on {ENTITY_LABELS[showDetails?.entityType]} #{showDetails?.entityId}
            </DialogDescription>
          </DialogHeader>
          {showDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-semibold text-gray-500">Timestamp</p>
                  <p>{new Date(showDetails.timestamp).toLocaleString()}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-500">Admin</p>
                  <p>{showDetails.username}</p>
                </div>
              </div>
              
              {showDetails.oldValue && (
                <div>
                  <p className="font-semibold text-gray-500 mb-2">Before</p>
                  <pre className="bg-red-50 p-3 rounded-lg text-xs overflow-auto max-h-[200px]">
                    {JSON.stringify(showDetails.oldValue, null, 2)}
                  </pre>
                </div>
              )}
              
              {showDetails.newValue && (
                <div>
                  <p className="font-semibold text-gray-500 mb-2">After</p>
                  <pre className="bg-green-50 p-3 rounded-lg text-xs overflow-auto max-h-[200px]">
                    {JSON.stringify(showDetails.newValue, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Clear Logs Dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent data-testid="clear-logs-dialog">
          <DialogHeader>
            <DialogTitle>Clear Old Audit Logs</DialogTitle>
            <DialogDescription>
              This will permanently delete all audit logs older than 90 days. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearDialog(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleClearOldLogs}
              disabled={clearing}
              data-testid="confirm-clear-logs"
            >
              {clearing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
              Clear Logs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogPage;
